<template>
  <div class="vpp-details">
  <validation-observer ref="observer">
    <labeled-wrapper :label="$t('general') | capitalize">
      <b-row>
        <b-col cols="6">
          <distribution-detail-field
              :label="$t('calculate_vpp') | capitalize"
              :edit-mode="editMode && !connectTariffPeriod && !internationalTariffPeriod"
              vid="vppCalculateAutomatically"
              ref="vppCalculateAutomatically"
          >
            <template v-slot:value>
              {{ distribution.vpp.calculateAutomatically ? $t("automatic") : $t("manual") | capitalize }}
            </template>
            <template v-slot:input>
              <b-form-radio-group required v-model="distribution.vpp.calculateAutomatically">
                <b-form-radio :value="false">{{ $t("manual") | capitalize }}</b-form-radio>
                <b-form-radio :value="true">{{ $t("automatic") | capitalize }}</b-form-radio>
              </b-form-radio-group>
            </template>
          </distribution-detail-field>
        </b-col>
        <b-col>
          <distribution-detail-field
              :label="$t('vpp_type')"
              :edit-mode="editMode && !connectTariffPeriod"
              vid="vppStatus"
              ref="vppStatus"
          >
            <template v-slot:value v-if="distribution.vpp.status">
              {{
                distribution.vpp.status === distributionVppTypes.OFFICIAL ? $t("official") : $t("placeholder") | capitalize
              }}
            </template>
            <template v-slot:input>
              <b-form-select v-model="distribution.vpp.status" :options="vppTypeOptions"/>
            </template>
          </distribution-detail-field>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="6">
          <distribution-detail-field :label="$t('added_by') | capitalize" :value="distribution.vpp.addedBy || '-'"/>
        </b-col>
        <b-col>
          <distribution-detail-field :label="$t('added_at') | capitalize" :value="addedAtValue"/>
        </b-col>
      </b-row>
    </labeled-wrapper>
    <labeled-wrapper :label="$t('vpp_amounts') | capitalize">
      <b-row>
        <b-col cols="6"
               v-if="this.distribution.college === distributionCollegeTypes.PERFORMER || this.distribution.college === distributionCollegeTypes.MAKER_AND_PERFORMER">
          <distribution-detail-field
              :label="$t('performer_vpp')"
              vid="vppPerformer"
              ref="vppPerformer"
              rules="double|positive"
              :value="distribution.vpp.performer"
              :edit-mode="editMode && !distribution.vpp.calculateAutomatically"
          >
            <template v-slot:value v-if="distribution.revenuePerformer">
              {{ distribution.vpp.performer | formatNumber(2) }}
            </template>
            <template v-slot:input="{ state }">
              <b-form-input v-model="distribution.vpp.performer" :state="state"/>
            </template>
          </distribution-detail-field>
        </b-col>
        <b-col cols="6"
               v-if="this.distribution.college === distributionCollegeTypes.MAKER || this.distribution.college === distributionCollegeTypes.MAKER_AND_PERFORMER">
          <distribution-detail-field
              :label="$t('maker_vpp')"
              vid="vppMaker"
              ref="vppMaker"
              rules="double|positive"
              :value="distribution.vpp.maker"
              :edit-mode="editMode && !distribution.vpp.calculateAutomatically"
          >
            <template v-slot:value v-if="distribution.revenuePerformer">
              {{ distribution.vpp.maker | formatNumber(2) }}
            </template>
            <template v-slot:input="{ state }">
              <b-form-input v-model="distribution.vpp.maker" :state="state"/>
            </template>
          </distribution-detail-field>
        </b-col>
      </b-row>
    </labeled-wrapper>
    <labeled-wrapper
        :label="$t('playcount') | capitalize"
        v-if="distribution.vpp.calculateAutomatically && distribution.distributionMethodology === distributionMethodologies.STANDARD">
      <b-row>
        <b-col cols="6">
          <distribution-detail-field
              :label="$t('playcounts_ineligible_percentage_display')"
              :edit-mode="editMode"
              vid="playcountsIneligiblePercentage"
              ref="playcountsIneligiblePercentage"
              rules="required|double|min_value:0|max_value:100"
          >
            <template v-slot:input="{ state }">
              <b-form-input v-model="distribution.playcountsIneligiblePercentage" :state="state"/>
            </template>
            <template v-if="distribution.playcountsIneligiblePercentage" v-slot:value>
              {{ distribution.playcountsIneligiblePercentage | formatNumber }}
            </template>
          </distribution-detail-field>
        </b-col>
        <b-col cols="6">
          <distribution-detail-field
              :label="$t('playcounts_unmatched_percentage_display')"
              :edit-mode="editMode"
              vid="playcountsUnmatchedPercentage"
              ref="playcountsUnmatchedPercentage"
              rules="required|double|min_value:0|max_value:100"
          >
            <template v-slot:input="{ state }">
              <b-form-input v-model="distribution.playcountsUnmatchedPercentage" :state="state"/>
            </template>
            <template v-if="distribution.playcountsUnmatchedPercentage" v-slot:value>
              {{ distribution.playcountsUnmatchedPercentage | formatNumber }}
            </template>
          </distribution-detail-field>
        </b-col>
      </b-row>
      <template v-if="distribution.vpp.playcounts">
        <b-row>
          <b-col cols="6">
            <distribution-detail-field
                :label="$t('playcounts_used_display')"
                vid="vppPlaycountsUsedForCalculation"
                ref="vppPlaycountsUsedForCalculation"
            >
              <template v-if="distribution.vpp.playcounts.usedForCalculation" v-slot:value>
                {{ distribution.vpp.playcounts.usedForCalculation | formatNumber }}
              </template>
            </distribution-detail-field>
          </b-col>
          <b-col cols="6">
            <distribution-detail-field
                :label="$t('playcounts_eligible') | capitalize"
                vid="vppPlaycountsEligible"
                ref="vppPlaycountsEligible"
            >
              <template v-if="distribution.vpp.playcounts.eligible" v-slot:value>
                {{ distribution.vpp.playcounts.eligible | formatNumber }}
              </template>
            </distribution-detail-field>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="6">
            <distribution-detail-field
                :label="$t('playcounts_ineligible') | capitalize"
                vid="vppPlaycountsIneligible"
                ref="vppPlaycountsIneligible"
            >
              <template v-if="distribution.vpp.playcounts.ineligible" v-slot:value>
                {{ distribution.vpp.playcounts.ineligible | formatNumber }}
              </template>
            </distribution-detail-field>
          </b-col>
          <b-col cols="6">
            <distribution-detail-field
                :label="$t('playcounts_unmatched') | capitalize"
                vid="vppPlaycountsUnmatched"
                ref="vppPlaycountsUnmatched"
            >
              <template v-if="distribution.vpp.playcounts.unmatched" v-slot:value>
                {{ distribution.vpp.playcounts.unmatched | formatNumber }}
              </template>
            </distribution-detail-field>
          </b-col>
        </b-row>
      </template>
    </labeled-wrapper>
  </validation-observer>
  </div>
</template>

<script>
import { distributionCollegeTypes, distributionMethodologies, distributionVppTypes } from "@/constants"
import DistributionDetailField from "@/components/DistributionDetailField"
import LabeledWrapper from "../../../components/LabeledWrapper.vue"
import { ValidationObserver } from "vee-validate"
import { capitalize } from "lodash"
import { distributionSectionMixin } from "@/utils/mixins"
import parseDate from "../../../utils/date-parser"

export default {
  name: "VPPSection",
  components: { LabeledWrapper, DistributionDetailField, ValidationObserver },
  mixins: [distributionSectionMixin],
  props: {
    distribution: Object,
    editMode: Boolean,
    connectTariffPeriod: Boolean,
    internationalTariffPeriod: Boolean,
  },
  computed: {
    distributionMethodologies () {
      return distributionMethodologies
    },
    addedAtValue () {
      return this.getFormatedTime(this.distribution.vpp.addedAt)
    }
  },
  data () {
    return {
      distributionCollegeTypes,
      distributionVppTypes,
      vppTypeOptions: [
        {
          text: capitalize(this.$t("official")),
          value: distributionVppTypes.OFFICIAL,
        },
        {
          text: capitalize(this.$t("placeholder")),
          value: distributionVppTypes.PLACEHOLDER,
        },
      ],
    }
  },
  methods: {
    getFormatedTime (time) {
      if (!time) {
        return "-"
      }
      return parseDate(time, this.$config.DATETIME_FORMAT)
    }
  },
  watch: {
    "distribution.playcountsIneligiblePercentage": function (newVal) {
      if (newVal !== this.distribution.vpp.playcountsIneligiblePercentage)
      {
        this.distribution.vpp.playcountsIneligiblePercentage = newVal
      }
    },
    "distribution.playcountsUnmatchedPercentage": function (newVal) {
      if (newVal !== this.distribution.vpp.playcountsUnmatchedPercentage)
      {
        this.distribution.vpp.playcountsUnmatchedPercentage = newVal
      }
    },
  }
}
</script>
